/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Chip } from '@mui/material';
import TestIds from '../../../../../testIds';
import { AccountMenuItem } from '../../../../azure-b2c/components/AccountSwitcher';
import { DigitalFarmerAccount, BasicDigitalFarmerAccount } from '../../../../azure-b2c/storage';
import { getAccountName } from '../../../utils/getAccountName';
import { TFOQuoteOfferScreenProps } from '../types';
import Autocomplete from '../../../../../components/Autocomplete';
import { colors } from '@farmlands/mui-theme';

export interface MultiAccountSelectorProps {
  accountList: BasicDigitalFarmerAccount[];
  farmerAccounts: BasicDigitalFarmerAccount[];
  defaultAccount?: DigitalFarmerAccount;
  showEmail?: boolean;
  setAccountList: TFOQuoteOfferScreenProps['setAccountList'];
}

export const MultiAccountSelector: React.FC<MultiAccountSelectorProps> = ({
  showEmail,
  accountList,
  farmerAccounts,
  setAccountList,
  defaultAccount,
}: MultiAccountSelectorProps) => {
  const currentAccount = defaultAccount;
  return (
    <Box>
      <Autocomplete
        slotProps={{
          popper: {
            sx: {
              zIndex: 9999,
            },
          },
        }}
        onChange={(_event, value) => {
          if (Array.isArray(value)) {
            setAccountList(value as BasicDigitalFarmerAccount[]);
          }
        }}
        disableClearable={accountList.length === 1}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.accountName}
                {...tagProps}
                onDelete={
                  option.accountNumber === currentAccount?.accountNumber
                    ? undefined
                    : tagProps.onDelete
                }
                // disabled={option.accountNumber === currentAccount?.accountNumber}
              />
            );
          })
        }
        testId={TestIds.components.address.dairySupplyInput}
        label='Send to'
        options={farmerAccounts}
        multiple
        value={accountList}
        getOptionLabel={(option) => {
          return getAccountName(option);
        }}
        disableCloseOnSelect
        renderOption={(props, option, state) => (
          <Box
            component='li'
            {...props}
            onSelect={
              option.accountNumber === currentAccount?.accountNumber ? undefined : props.onSelect
            }
            onClick={
              option.accountNumber === currentAccount?.accountNumber ? undefined : props.onClick
            }
            onSelectCapture={
              option.accountNumber === currentAccount?.accountNumber
                ? undefined
                : props.onSelectCapture
            }
            onClickCapture={
              option.accountNumber === currentAccount?.accountNumber
                ? undefined
                : props.onClickCapture
            }
            // This is to reset sx and className
            sx={{ mt: 2 }}
            className=''
            key={`${option.accountNumber.toString()}-${option.accountName}`}
          >
            <AccountMenuItem
              selected={state.selected}
              showEmail={showEmail}
              bolden={false}
              item={option}
              onChange={() => null}
            />
          </Box>
        )}
        textFieldProps={{
          helperText: 'Select additional accounts to send the quote to more people',
          helperTextColor: colors.grey700,
        }}
      />
    </Box>
  );
};
